// Marketing › Ads and calls in Blueprint (pages/ux-full screens/ads.js): who sees the pages, the periods they
// read, and today's calls by source. The views are ui-web's (AdsViews.tsx), each drawn from its action's
// answer; the server counts everything.
import { channelWord } from '@flowology/ui-web';
import { addDays } from '../books/model.ts';
import { everyPage } from '../shared/pages.ts';

/**
 * Who sees the ads: whoever holds marketing.view (the owner and the manager, and anyone a company's own
 * role gives it: a marketing person, QA-681), by the permission, never a role name.
 */
export const ADS_PERMISSION = 'marketing.view';
/** Who sees what was sent to Google (adConversions.list): marketing.manage. */
export const CONVERSION_PERMISSION = 'marketing.manage';

export const mayAds = (held: readonly string[]): boolean => held.includes(ADS_PERMISSION);
export const maySeeConversions = (held: readonly string[]): boolean => held.includes(CONVERSION_PERMISSION);

export type Period = 'today' | 'week' | 'month';
export const PERIODS: [Period, string][] = [
  ['today', 'Today'],
  ['week', 'This week'],
  ['month', 'This month'],
];

/** A period of the company's days, to today: the week from its Monday, the month from its first. */
export function periodRange(period: Period, today: string): { from: string; to: string } {
  if (period === 'today') return { from: today, to: today };
  if (period === 'month') return { from: `${today.slice(0, 8)}01`, to: today };
  const weekday = (new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7; // Monday is 0
  return { from: addDays(today, -weekday), to: today };
}

/** The month a day is in, first to last: adSpend.list reads it whole, and spreads it to today. */
export function monthRange(today: string): { from: string; to: string } {
  const [y, m] = today.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${today.slice(0, 8)}01`, to: `${today.slice(0, 8)}${String(last).padStart(2, '0')}` };
}

// ── Over time (reports.campaigns by day or week, BL-091: AdTrack's report by day and week) ──

export type TrendPreset = '7' | '30' | '90' | 'custom';
export const TREND_PRESETS: [TrendPreset, string][] = [
  ['7', '7 days'],
  ['30', '30 days'],
  ['90', '90 days'],
  ['custom', 'Pick days'],
];
/** Up to this many days a row each; longer reads by week. */
export const TREND_DAYS_BY_DAY = 62;
/** The longest range picked by hand. */
export const TREND_MAX_DAYS = 731;

const daysIn = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000) + 1;

/**
 * The company's days a preset reads, to today, a row each day, or each week when there are more than
 * two months of them; picked by hand, its own days, or what's wrong with them.
 */
export function trendRange(
  preset: TrendPreset,
  today: string,
  picked: { from: string; to: string } = { from: '', to: '' },
): { from: string; to: string; groupBy: 'day' | 'week' } | { error: string } {
  const range = preset === 'custom' ? picked : { from: addDays(today, -(Number(preset) - 1)), to: today };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(range.from) || !/^\d{4}-\d{2}-\d{2}$/.test(range.to))
    return { error: 'Pick the first and the last day.' };
  if (range.to < range.from) return { error: 'The last day is before the first.' };
  const n = daysIn(range.from, range.to);
  if (n > TREND_MAX_DAYS) return { error: 'Pick two years or less.' };
  return { ...range, groupBy: n > TREND_DAYS_BY_DAY ? 'week' : 'day' };
}

/** calls.recent's count of one source's calls (`bySource`): its campaign channel, or 'none'. */
export interface SourceCount {
  channel: string;
  n: number;
}

/**
 * The sources today's calls came from, most first, each with its count, after "All": the server's counts
 * of every call (list caps, BL-110), not of the pages read so far.
 */
export function sources(counts: readonly SourceCount[]): { key: string; label: string; n: number }[] {
  return [
    { key: 'all', label: 'All', n: counts.reduce((sum, c) => sum + c.n, 0) },
    ...[...counts]
      .sort((a, b) => b.n - a.n)
      .map((c) => ({ key: c.channel, label: channelWord(c.channel === 'none' ? null : c.channel), n: c.n })),
  ];
}

/** Calls by source reads a hundred of today's inbound calls at a time. */
export const LEADS_PAGE = 100;

/** Today's inbound calls, as the server filters them: from one source, or all (list caps, BL-110). */
export const leadsInput = (today: string, source: string): Record<string, unknown> => ({
  from: today,
  to: today,
  direction: 'inbound',
  limit: LEADS_PAGE,
  ...(source === 'all' ? {} : { channel: source }),
});

/** Each source's count of today's inbound calls, counted by the server over all of them (one row read). */
export const sourceCountsInput = (today: string): Record<string, unknown> => ({
  from: today,
  to: today,
  direction: 'inbound',
  limit: 1,
  bySource: true,
});

// ── Suggested ad changes (adChanges.*) ──

/** Who decides a suggested ad change, runs the review and marks one done (marketing.manage). */
export const mayDecideAdChanges = (held: readonly string[]): boolean => held.includes('marketing.manage');

/** The queue: what waits for a decision, and what's approved and still to make in the platform. */
export const OPEN_CHANGES = ['waiting', 'approved'] as const;
/** What's closed: dismissed with why not, or made in the platform and marked done. */
export const CLOSED_CHANGES = ['dismissed', 'done'] as const;

/** What the review did, in a sentence: what it found, what's new, and what it left alone. */
export function reviewWords(r: { found: number; added: number; refreshed: number; quiet: number }): string {
  if (!r.found) return 'The review found nothing to change.';
  const parts = [
    r.added ? `${r.added} new` : null,
    r.refreshed ? `${r.refreshed} already waiting, brought up to date` : null,
    r.quiet ? `${r.quiet} decided lately, left alone` : null,
  ].filter(Boolean);
  return `The review found ${r.found === 1 ? 'one change' : `${r.found} changes`}: ${parts.join(', ')}.`;
}

/** Home's line for changes waiting, or null with none. */
export const waitingChangesLine = (n: number): string | null =>
  n <= 0
    ? null
    : n === 1
      ? 'A suggested ad change waits for a decision.'
      : `${n} suggested ad changes wait for a decision.`;

/** adSpend.list's answer, a page at a time. */
export interface SpendPage<E> {
  entries: E[];
  next: string | null;
}

/**
 * Every ad spend entry in a range, page after page (list caps M11): the month's spend and its
 * projection add up every entry, so a page of them would understate it.
 */
export function allSpend<E>(
  read: (input: Record<string, unknown>) => Promise<SpendPage<E>>,
  range: { from: string; to: string },
): Promise<E[]> {
  return everyPage<E, string>(async (after) => {
    const p = await read({ ...range, ...(after ? { after } : {}) });
    return { items: p.entries, next: p.next };
  });
}
